import * as THREE from 'three'
import { injectSunHaze } from './atmosphere'
import { mustReplace } from './shaderPatch'

/**
 * The conifer material, now carrying the photographic atlas.
 *
 * Alpha-tested rather than blended: cutout foliage keeps depth writes sane in
 * any draw order and works in the shadow pass, which blended foliage never
 * does. alphaTest 0.5 with the matting the background remover produced gives
 * clean needle edges; the composer's anti-aliasing softens the cut.
 *
 * The needle scattering survives from the procedural era — a crown is a sparse
 * volume, light leaks through it and rims it against the sun — and it still
 * keys off the diffuse green so the snow in the photos does not glow like
 * styrofoam. See the pars-chunk note below for why the patch expands three's
 * lighting include by hand.
 */

let atlas: THREE.Texture | null = null
const readyCallbacks: Array<() => void> = []
let atlasReady = false

function loadAtlas(): THREE.Texture {
  if (atlas) return atlas
  atlas = new THREE.TextureLoader().load('tex/trees.png', () => {
    atlasReady = true
    for (const cb of readyCallbacks) cb()
    readyCallbacks.length = 0
  })
  atlas.colorSpace = THREE.SRGBColorSpace
  atlas.anisotropy = 4
  // Cards live at cell edges; clamping avoids bleeding the neighbour cell in
  // at mip level where wrap would.
  atlas.wrapS = atlas.wrapT = THREE.ClampToEdgeWrapping
  return atlas
}

/**
 * Runs cb once the atlas pixels are actually decoded. Until then a tree drawn
 * with the material samples an empty texture — black cards, alpha zero, the
 * whole forest invisible or wrong — so the scene keeps tree meshes hidden and
 * flips them visible from here.
 */
export function onTreeAtlasReady(cb: () => void): void {
  if (atlasReady) cb()
  else readyCallbacks.push(cb)
}

export function createTreeMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    map: loadAtlas(),
    vertexColors: true,
    roughness: 0.82,
    metalness: 0,
    alphaTest: 0.5,
    side: THREE.DoubleSide,
  })

  mat.onBeforeCompile = (shader) => {
    injectSunHaze(shader)

    const scatterChunk = mustReplace(
      THREE.ShaderChunk.lights_physical_pars_fragment,
      'vec3 irradiance = dotNL * directLight.color;',
      [
        '// Wrapped diffuse: a crown has no hard terminator, because the light',
        '// that lit the front of it is still arriving at the back.',
        'float flWrap = saturate( ( dot( geometryNormal, directLight.direction )',
        '  + FL_LEAF_WRAP ) / ( 1.0 + FL_LEAF_WRAP ) );',
        'vec3 irradiance = flWrap * directLight.color;',
        '// Forward scatter, masked to the green needle areas of the photo so',
        '// the snow loaves do not glow from behind.',
        'float flBack = saturate( dot( geometryViewDir, -directLight.direction ) );',
        '// material.diffuseColor, not diffuseColor: this is a *pars* chunk,',
        '// declared at global scope where main()\'s locals do not exist.',
        'float flNeedle = 1.0 - smoothstep( 0.30, 0.62, material.diffuseColor.g );',
        'irradiance += directLight.color * pow( flBack, 2.0 )',
        '  * FL_LEAF_TRANSMIT * flNeedle;',
      ].join('\n'),
      'needle scattering',
    )
    shader.fragmentShader = mustReplace(
      shader.fragmentShader,
      '#include <lights_physical_pars_fragment>',
      [
        '#define FL_LEAF_WRAP 0.55',
        '#define FL_LEAF_TRANSMIT 0.45',
        scatterChunk,
      ].join('\n'),
      'needle scattering chunk',
    )
  }

  mat.customProgramCacheKey = () => 'fall-line-tree-cards'
  return mat
}

/**
 * The matching shadow-pass material. Without it every card casts the shadow
 * of its full quad and the forest floor turns into a field of dark rectangles;
 * with the map and the same alpha test, shadows are cut to the needles.
 */
export function createTreeDepthMaterial(): THREE.MeshDepthMaterial {
  return new THREE.MeshDepthMaterial({
    depthPacking: THREE.RGBADepthPacking,
    map: loadAtlas(),
    alphaTest: 0.5,
  })
}
